<template>
  <div>
    <button
      :class="{
        'tw-group tw-w-full': true,
        'tw-p-2': allowSelection,
        'hover:tw-opacity-75': allowSelection && !isRenamingUIVisible,
        'tw-bg-deep-orange-200 tw-p-2 hover:tw-bg-deep-orange-200 hover:tw-bg-opacity-70': isSelected
      }"
      :disabled="!allowSelection || isRenamingUIVisible"
      @click="handleSelectVideoAsset"
    >
      <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2">
        <div
          v-if="!showDefaultThumbnail"
          :class="{
            'tw-aspect-video tw-h-10 tw-rounded tw-bg-cover tw-bg-center': true,
            'tw-mb-3': isRenamingUIVisible
          }"
          :style="{ backgroundImage: `url(${videoAsset?.urls?.thumbnail})` }"
        >
          <img
            :src="videoAsset?.urls?.thumbnail"
            alt="Fallback Thumbnail"
            style="display: none"
            @error="handleThumbnailError"
          />
        </div>
        <div
          v-else
          class="tw-aspect-video tw-h-10 tw-self-start tw-rounded tw-bg-slate-100 tw-object-cover dark:tw-bg-slate-700"
        ></div>

        <div v-if="isRenamingUIVisible" class="tw-w-full">
          <form @submit.prevent="handleRenameVideoAsset">
            <StudioInput
              id="video-asset-new-name-input"
              ref="newNameInput"
              v-model="newName"
              :maxlength="255"
              required
            />
            <div class="tw-mt-2 tw-flex tw-w-full tw-justify-end tw-gap-2">
              <StudioButton
                trackingId="cancel-renaming-video-item-button"
                size="small"
                variant="outline"
                :disabled="isRenaming"
                @click="hideRenameUI"
              >
                Cancel
              </StudioButton>
              <StudioButton
                trackingId="save-renaming-video-item-button"
                size="small"
                type="submit"
                :disabled="isVisibleActionsDisabled || isRenaming"
              >
                {{ isRenaming ? 'Saving...' : 'Save' }}
              </StudioButton>
            </div>
          </form>
        </div>
        <div v-else class="tw-flex-1 tw-text-left" data-testid="video-library-clip-item">
          <div class="tw-mb-1 tw-break-words tw-break-all tw-text-sm">
            {{ videoAsset.name }}
          </div>
          <div class="tw-text-xs tw-text-gray-500">
            <template v-if="videoAsset.status === 'COMPLETE'">
              {{ videoAsset.duration }}
            </template>
            <template v-else-if="videoAsset.status === 'ERROR'"> Error </template>
            <template v-else> Processing... </template>
          </div>
        </div>

        <StudioButtonGroup
          v-if="!isRenamingUIVisible && !$slots.actions"
          variant="outline"
          size="small"
          :class="alwaysShowActions ? '' : 'tw-hidden group-hover:tw-flex'"
          :buttons="actionButtons"
        />
        <slot name="actions" />
      </div>
      <StudioAlertBanner v-if="videoAsset.errors.length > 0" type="error" class="tw-mt-2">
        <div v-for="error in videoAsset.errors" :key="error" class="tw-mb-1">
          {{ error }}
        </div>
      </StudioAlertBanner>
      <StudioAlertBanner v-if="videoAsset.warnings.length > 0" type="warning" class="tw-mt-2">
        <div v-for="warning in videoAsset.warnings" :key="warning" class="tw-mb-1">
          {{ warning }}
        </div>
      </StudioAlertBanner>
    </button>
    <StudioConfirmationDialog
      v-if="isConfirmingDelete"
      type="error"
      heading="Are you sure?"
      :actionsDisabled="isDeleting"
      :message="`You are deleting the video <b>${videoAsset.name}</b>. This action cannot be undone.`"
      :primaryActionTitle="isDeleting ? 'Deleting...' : 'Delete'"
      secondaryActionTitle="Cancel"
      @primaryAction="handleDeleteVideoAsset"
      @secondaryAction="hideDeleteConfirmationDialog"
    />
    <HLSVideoPreviewDialog
      v-if="hlsPreviewVideoAsset"
      :videoAsset="hlsPreviewVideoAsset"
      @close="hlsPreviewVideoAsset = null"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { mapActions } from 'vuex';

import HLSVideoPreviewDialog from './HLSVideoPreviewDialog.vue';
import { VideoAsset, deleteVideoAsset, renameVideoAsset, downloadFileFromUrl } from './videoLibraryService';

import TablerDownload from '@/studioComponents/Icons/tabler/TablerDownload.vue';
import TablerEdit from '@/studioComponents/Icons/tabler/TablerEdit.vue';
import TablerPlayerPlay from '@/studioComponents/Icons/tabler/TablerPlayerPlay.vue';
import TablerReplace from '@/studioComponents/Icons/tabler/TablerReplace.vue';
import TablerTrash from '@/studioComponents/Icons/tabler/TablerTrash.vue';
import TablerX from '@/studioComponents/Icons/tabler/TablerX.vue';
import StudioAlertBanner from '@/studioComponents/StudioAlertBanner.vue';
import StudioButton from '@/studioComponents/StudioButton.vue';
import StudioButtonGroup, { StudioButtonGroupButtonProps } from '@/studioComponents/StudioButtonGroup.vue';
import StudioConfirmationDialog from '@/studioComponents/StudioConfirmationDialog.vue';
import StudioInput from '@/studioComponents/StudioInput.vue';

type VisibleActions = {
  remove?: boolean;
  rename?: boolean;
  download?: boolean;
  openPreview?: boolean;
  delete?: boolean;
  replace?: boolean;
};

export default defineComponent({
  name: 'VideoLibraryListItem',
  components: {
    StudioAlertBanner,
    StudioInput,
    StudioConfirmationDialog,
    HLSVideoPreviewDialog,
    StudioButtonGroup,
    StudioButton
  },
  props: {
    videoAsset: {
      type: Object as PropType<VideoAsset>,
      required: true
    },
    allowSelection: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    visibleActions: {
      type: Object as PropType<VisibleActions>,
      default: () => ({
        remove: false,
        rename: false,
        download: false,
        openPreview: false,
        delete: false,
        replace: false
      })
    },
    showActionTitle: {
      type: Boolean,
      default: false
    },
    alwaysShowActions: {
      type: Boolean,
      default: false
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    isVisibleActionsDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isRenaming: false,
      isRenamingUIVisible: false,
      isConfirmingDelete: false,
      isDeleting: false,
      isDownloading: false,
      hlsPreviewVideoAsset: null as VideoAsset | null,
      newName: this.videoAsset.name || '',
      showDefaultThumbnail: false
    };
  },
  computed: {
    actionButtons(): StudioButtonGroupButtonProps[] {
      const videoPreviewDisabled = !this.videoAsset?.urls?.hls || this.videoAsset.status !== 'COMPLETE';

      return [
        {
          title: 'Rename',
          label: this.showActionTitle ? 'Rename' : '',
          hidden: !this.visibleActions.rename || this.videoAsset.creationType === 'RECORDED',
          clickHandler: this.showRenameUI,
          disabled: this.isRenaming || this.isVisibleActionsDisabled || this.isSaving,
          iconComponent: TablerEdit,
          trackingId: 'video-library-list-item-rename-button'
        },
        {
          title: videoPreviewDisabled
            ? 'Video is not ready yet. Please try again after some time.'
            : 'Open video preview',
          label: this.showActionTitle ? 'Preview' : '',
          hidden: !this.visibleActions.openPreview,
          clickHandler: this.openVideoAssetPreview,
          disabled: videoPreviewDisabled,
          iconComponent: TablerPlayerPlay,
          trackingId: 'video-library-list-item-preview-button'
        },
        {
          title: 'Download',
          label: this.showActionTitle ? 'Download' : '',
          hidden: !this.visibleActions.download,
          clickHandler: this.handleDownloadVideo,
          disabled: !this.videoAsset?.urls?.mp4 || this.isDownloading,
          iconComponent: TablerDownload,
          trackingId: 'video-library-list-item-download-button'
        },
        {
          title: 'Delete',
          label: this.showActionTitle ? 'Delete' : '',
          hidden: !this.visibleActions.delete || this.videoAsset.creationType === 'RECORDED',
          clickHandler: this.showDeleteConfirmationDialog,
          disabled: this.isDeleting || this.isVisibleActionsDisabled,
          iconComponent: TablerTrash,
          trackingId: 'video-library-list-item-delete-button'
        },
        {
          title: 'Remove',
          isLoading: this.isSaving,
          label: this.showActionTitle ? 'Remove' : '',
          hidden: !this.visibleActions.remove,
          clickHandler: this.handleRemoveVideoAsset,
          disabled: this.isVisibleActionsDisabled || this.isSaving,
          iconComponent: TablerX,
          trackingId: 'video-library-list-item-remove-button'
        },
        {
          title: 'Replace',
          label: this.showActionTitle ? 'Replace' : '',
          hidden: !this.visibleActions.replace,
          clickHandler: this.handleReplaceVideoAsset,
          disabled: this.isVisibleActionsDisabled,
          iconComponent: TablerReplace,
          trackingId: 'video-library-list-item-replace-button'
        }
      ];
    }
  },
  methods: {
    ...mapActions('studioToast', { triggerToast: 'triggerToast' }),
    openVideoAssetPreview(e: Event | undefined): void {
      if (e) {
        e.stopPropagation();
      }

      if (!this.videoAsset?.urls?.hls) {
        this.triggerToast({
          mode: 'error',
          msg: 'Video is not ready yet. Please try again later.'
        });
        return;
      }

      this.hlsPreviewVideoAsset = this.videoAsset;
    },
    handleDownloadVideo(e: Event | undefined): void {
      if (e) {
        e.stopPropagation();
      }

      if (!this.videoAsset?.urls?.mp4) {
        return;
      }

      this.isDownloading = true;
      downloadFileFromUrl(this.videoAsset.urls.mp4, this.videoAsset.name, 'mp4')
        .then(() => {
          this.triggerToast({ mode: 'success', msg: 'Video downloaded' });
        })
        .catch(() => {
          this.triggerToast({ mode: 'error', msg: 'Failed to download video' });
        })
        .finally(() => {
          this.isDownloading = false;
        });
    },
    showRenameUI(e: Event | undefined): void {
      if (e) {
        e.stopPropagation();
      }

      this.newName = this.videoAsset.name;
      this.isRenamingUIVisible = true;
    },
    hideRenameUI(e?: Event): void {
      if (e) {
        e.stopPropagation();
      }

      this.isRenamingUIVisible = false;
    },
    handleRenameVideoAsset(e: Event | undefined): void {
      if (e) {
        e.stopPropagation();
      }

      const newNameInput: any = this.$refs?.newNameInput;

      if (!newNameInput) {
        return;
      }

      if (!newNameInput.checkValidity()) {
        newNameInput.focus();
        return;
      }

      if (!this.newName.trim()) {
        this.triggerToast({
          mode: 'error',
          msg: 'Video name cannot be empty.'
        });
        return;
      }

      this.isRenaming = true;
      renameVideoAsset(this.videoAsset.id, this.newName)
        .then((newVideoAsset: VideoAsset) => {
          this.triggerToast({
            mode: 'success',
            msg: 'Video renamed successfully.'
          });
          this.hideRenameUI();
          this.$emit('rename-success', newVideoAsset);
        })
        .catch((errorMessage: string) => {
          this.triggerToast({
            mode: 'error',
            msg: errorMessage
          });
        })
        .finally(() => {
          this.isRenaming = false;
        });
    },
    handleDeleteVideoAsset(): void {
      this.isDeleting = true;
      deleteVideoAsset(this.videoAsset.id)
        .then(() => {
          this.triggerToast({
            mode: 'success',
            msg: 'Video deleted successfully.'
          });
          this.hideDeleteConfirmationDialog();
          this.$emit('delete-success', this.videoAsset);
        })
        .catch((errorMessage: string) => {
          this.triggerToast({ mode: 'error', msg: errorMessage });
        })
        .finally(() => {
          this.isDeleting = false;
        });
    },
    handleRemoveVideoAsset(e: Event | undefined): void {
      if (e) {
        e.stopPropagation();
      }

      this.$emit('remove', this.videoAsset);
    },
    showDeleteConfirmationDialog(e: Event | undefined) {
      if (e) {
        e.stopPropagation();
      }

      this.isConfirmingDelete = true;
    },
    hideDeleteConfirmationDialog() {
      this.isConfirmingDelete = false;
    },
    handleSelectVideoAsset(): void {
      this.$emit('select-video', this.videoAsset);
    },
    handleReplaceVideoAsset(): void {
      this.$emit('replace', this.videoAsset);
    },
    handleThumbnailError(): void {
      this.showDefaultThumbnail = true;
    }
  }
});
</script>
